import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { postAnalysis, loadErrorMessage } from './useProject';
import { startAnalysis, takeAnalysis, useAnalysis, updateAnalysis, uploadProgress, analysisStep, markFresh } from './analysisStore';
import { trackAnalysis } from '../utils/analytics';
import { FULL_MIX } from '../utils/instruments';
import { readWaveform } from '../utils/clipWaveform';

// Add a version to the clip's list, unless the list has it already (a reload can beat the result)
const withVersion = (versions, version) => (
  versions.some((v) => v.id === version.id) ? versions : [...versions, version]
);

/**
 * One clip: its summary, all its versions (oldest first) and its chat messages.
 * - addVersion({ file, note }) analyzes the next version; `pending` describes it while it runs, and
 *   stays when this clip is closed and opened again (the analysis lives in analysisStore)
 * - update(changes) renames or retags it; remove() deletes it
 * onChange gets the clip's new summary after each change, and onCredits the credits left; those
 * also hear an analysis that finishes after this clip was closed. `failure` is why the last
 * analysis failed, until clearFailure() once it's been shown.
 */
export const useClip = (clipId, { onChange, onCredits } = {}) => {
  const { authFetch } = useAuth();
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const analysis = useAnalysis(clipId);
  const current = useRef(clipId);
  current.current = clipId;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authFetch(`/clips/${clipId}`);
      if (current.current === clipId) setClip(data);
    } catch (err) {
      if (current.current === clipId) {
        setClip(null);
        setError(loadErrorMessage(err, 'clip'));
      }
    } finally {
      if (current.current === clipId) setLoading(false);
    }
  }, [clipId, authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  // A finished analysis joins the thread, whether it was started here or before the clip was reopened
  useEffect(() => {
    if (analysis?.status !== 'done' || !clip) return;
    const { result } = analysis;
    setClip((prev) => prev && { ...prev, ...result.clip, versions: withVersion(prev.versions, result.version) });
    takeAnalysis(clipId);
  }, [analysis, clip, clipId]);

  const nextVersion = (clip?.latest_version || 0) + 1;

  /** Analyze the clip's next version; resolves to the new version, or throws with the reason */
  const addVersion = useCallback(async ({ file, note }) => {
    const info = {
      filename: file.name,
      note,
      version: nextVersion,
      startedAt: Date.now(),
      name: clip?.name,
      category: clip?.category,
      instrumentType: clip?.instrument_type,
    };
    const running = startAnalysis(
      clipId,
      info,
      trackAnalysis(
        postAnalysis(authFetch, `/clips/${clipId}/versions`, { file, note }, { onUploadProgress: uploadProgress(clipId), onEvent: analysisStep(clipId) }),
        { kind: clip?.category === FULL_MIX ? 'mix' : 'stem', category: clip?.category, version: nextVersion }
      ).then((done) => {
        markFresh(done.version.id);
        // The project hears it even if this clip has been closed by now
        onChange?.(done.clip);
        if (done.credits_remaining !== undefined) onCredits?.(done.credits_remaining);
        return done;
      })
    );
    readWaveform(file).then((waveform) => updateAnalysis(clipId, { waveform }));
    return (await running).version;
  }, [authFetch, clipId, clip?.category, clip?.name, clip?.instrument_type, nextVersion, onChange, onCredits]);

  const update = useCallback(async (changes) => {
    const summary = await authFetch(`/clips/${clipId}`, { method: 'PATCH', json: changes });
    setClip((prev) => prev && { ...prev, ...summary });
    onChange?.(summary);
    return summary;
  }, [authFetch, clipId, onChange]);

  const remove = useCallback(async () => {
    await authFetch(`/clips/${clipId}`, { method: 'DELETE' });
  }, [authFetch, clipId]);

  const appendMessages = useCallback((messages) => {
    setClip((prev) => prev && { ...prev, messages: [...prev.messages, ...messages] });
  }, []);

  const clearFailure = useCallback(() => takeAnalysis(clipId), [clipId]);

  return {
    clip,
    versions: clip?.versions || [],
    messages: clip?.messages || [],
    loading,
    error,
    pending: analysis?.status === 'running' ? analysis.info : null,
    failure: analysis?.status === 'failed' ? analysis.error : null,
    clearFailure,
    nextVersion,
    reload: load,
    addVersion,
    update,
    remove,
    appendMessages,
  };
};
